/**
 * Card — the portal's surface container.
 *
 * `framed` adds the gold double-line frame that gives the portal its prestige
 * look; `interactive` adds the hover lift used by clickable tiles. Renders a
 * <div> unless `onClick` is given, in which case it becomes a real <button> so
 * clickable cards stay keyboard-reachable.
 */

import type { HTMLAttributes, ReactNode } from 'react';
import styles from './card.module.css';

// A clickable Card renders as a <button>, so the pass-through props are typed
// against the common HTMLElement attributes rather than HTMLDivElement — the
// two elements' event handler types are not interchangeable.
interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'onClick'> {
  /** Gold double-line frame. */
  framed?: boolean;
  /** Recessed background, for panels nested inside another surface. */
  sunken?: boolean;
  /** Built-in padding; omit when the card lays out its own sections. */
  padded?: boolean;
  /**
   * The element to render. A card is a surface, not a semantic role, so the
   * markup around it varies: a campaign hero is a <header>, a portfolio entry
   * an <article>, a quiz a <form>. Ignored when `onClick` is set — a clickable
   * card is a <button> so it stays keyboard-reachable.
   */
  as?: 'div' | 'section' | 'article' | 'header' | 'aside' | 'li' | 'form';
  onClick?: () => void;
  /**
   * Only meaningful with `onClick`, which is what makes the card a <button>.
   * `disabled` is a ButtonHTMLAttributes member, so it cannot ride through the
   * pass-through props typed against HTMLElement.
   */
  disabled?: boolean;
  className?: string;
  children?: ReactNode;
}

export function Card({
  framed = false,
  sunken = false,
  padded = true,
  onClick,
  disabled,
  as: Element = 'div',
  className,
  children,
  ...rest
}: CardProps) {
  const composed = [
    styles.base,
    framed ? styles.framed : null,
    sunken ? styles.sunken : null,
    padded ? styles.padded : null,
    onClick ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (onClick) {
    return (
      <button type="button" className={composed} onClick={onClick} disabled={disabled} {...rest}>
        {children}
      </button>
    );
  }

  return (
    <Element className={composed} {...rest}>
      {children}
    </Element>
  );
}
